import { graphql, Link } from "gatsby"
import * as React from "react"

import Layout from "../components/layout"
import Seo from "../components/seo"

import "bootstrap/dist/css/bootstrap.min.css"
import "../articles.css"

const TOPIC_META = {
  fundamentals: { label: "Fundamentals", tone: "purple" },
  "best-practices": { label: "Best practices", tone: "green" },
  "code-reading": { label: "Review techniques", tone: "blue" },
  collaboration: { label: "Collaboration", tone: "coral" },
  checklists: { label: "Checklists", tone: "yellow" },
  automation: { label: "Automation", tone: "green" },
  tools: { label: "Tools", tone: "blue" },
  security: { label: "Security", tone: "coral" },
  companies: { label: "Industry practice", tone: "yellow" },
  devex: { label: "Developer experience", tone: "purple" },
  research: { label: "Research & evidence", tone: "yellow" },
  video: { label: "Video", tone: "blue" },
  general: { label: "General", tone: "purple" },
}

const TOPIC_ORDER = [
  "fundamentals",
  "best-practices",
  "code-reading",
  "collaboration",
  "checklists",
  "automation",
  "tools",
  "security",
  "companies",
  "devex",
  "research",
  "video",
  "general",
]

const SORT_LABELS = {
  topic: "Group by topic",
  title: "Title A–Z",
  recent: "Recently updated",
}

const normalizeTopic = topic =>
  String(topic || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-")

const topicDetails = topic => {
  if (TOPIC_META[topic]) {
    return TOPIC_META[topic]
  }

  return {
    label: topic
      .split("-")
      .filter(Boolean)
      .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
      .join(" "),
    tone: "purple",
  }
}

const articleTopics = topics => {
  const normalized = (topics || [])
    .map(topic => normalizeTopic(Array.isArray(topic) ? topic[0] : topic))
    .filter(Boolean)

  return normalized.length ? [...new Set(normalized)] : ["general"]
}

const twoSentenceSummary = value => {
  const summary = String(value || "")
    .replace(/\s+/g, " ")
    .trim()

  if (!summary) {
    return "A practical guide from the Awesome Code Reviews knowledge hub."
  }

  const protectedSummary = summary
    .replace(/\bDr\./g, "Dr∯")
    .replace(/\be\.g\./gi, "e∯g∯")
    .replace(/\bi\.e\./gi, "i∯e∯")
  const sentences = protectedSummary.match(/[^.!?]+(?:[.!?]+(?=\s|$)|$)/g) || []

  return sentences.slice(0, 2).join(" ").replace(/∯/g, ".").trim()
}

const sortByTitle = articles =>
  [...articles].sort((left, right) =>
    left.title.localeCompare(right.title, "en")
  )

const sortByRecent = articles =>
  [...articles].sort((left, right) => {
    const dateDifference =
      new Date(right.date || 0).getTime() - new Date(left.date || 0).getTime()

    return dateDifference || left.title.localeCompare(right.title, "en")
  })

const ArticleCard = ({ article }) => (
  <Link className="article-index-card" to={article.slug}>
    <div className="article-index-card__meta">
      <ul aria-label="Topics">
        {article.topics.map(topic => {
          const details = topicDetails(topic)

          return (
            <li
              className={`article-index-chip article-index-chip--${details.tone}`}
              key={topic}
            >
              {details.label}
            </li>
          )
        })}
      </ul>
      {article.date && (
        <time dateTime={article.date}>
          {new Intl.DateTimeFormat("en", {
            month: "short",
            year: "numeric",
          }).format(new Date(article.date))}
        </time>
      )}
    </div>
    <h3>{article.title}</h3>
    <p>{article.summary}</p>
    <span className="article-index-card__cta">
      Read article <span aria-hidden="true">→</span>
    </span>
  </Link>
)

const ArticlesPage = ({ data, location }) => {
  const siteTitle = data.site.siteMetadata?.title || "Awesome Code Reviews"
  const [activeTopic, setActiveTopic] = React.useState("all")
  const [sortOrder, setSortOrder] = React.useState("topic")

  const articles = React.useMemo(
    () =>
      data.allMarkdownRemark.nodes.map(node => {
        const topics = articleTopics(node.frontmatter.topics)

        return {
          title: node.frontmatter.title,
          slug: node.fields.slug,
          topics,
          primaryTopic: topics[0],
          summary: twoSentenceSummary(
            node.frontmatter.description ||
              node.frontmatter.excerpt ||
              node.generatedExcerpt
          ),
          date: node.frontmatter.updated || node.frontmatter.date,
        }
      }),
    [data.allMarkdownRemark.nodes]
  )

  const availableTopics = React.useMemo(
    () =>
      TOPIC_ORDER.map(topic => ({
        id: topic,
        count: articles.filter(article => article.topics.includes(topic))
          .length,
        ...topicDetails(topic),
      })).filter(topic => topic.count > 0),
    [articles]
  )

  const visibleArticles =
    activeTopic === "all"
      ? articles
      : articles.filter(article => article.topics.includes(activeTopic))

  let groups

  if (sortOrder === "topic" && activeTopic === "all") {
    groups = availableTopics
      .map(topic => ({
        id: topic.id,
        label: topic.label,
        tone: topic.tone,
        articles: sortByTitle(
          visibleArticles.filter(article => article.primaryTopic === topic.id)
        ),
      }))
      .filter(group => group.articles.length > 0)
  } else {
    const sortedArticles =
      sortOrder === "recent"
        ? sortByRecent(visibleArticles)
        : sortByTitle(visibleArticles)
    const activeDetails =
      activeTopic === "all"
        ? { label: "All articles", tone: "purple" }
        : topicDetails(activeTopic)

    groups = [
      {
        id: activeTopic,
        label: activeDetails.label,
        tone: activeDetails.tone,
        articles: sortedArticles,
      },
    ]
  }

  return (
    <Layout location={location} title={siteTitle}>
      <Seo
        title="All code review articles"
        description="Browse every published Awesome Code Reviews article, organized by topic with concise summaries."
      />

      <main className="article-index">
        <section className="article-index-hero">
          <div className="content">
            <div className="article-index-hero__copy">
              <p>Awesome Code Reviews library</p>
              <h1>Every article, clearly organized.</h1>
              <span>
                Browse the complete collection by topic, scan a short summary,
                and find the guide you need without digging.
              </span>
            </div>
            <div
              className="article-index-hero__stats"
              aria-label="Library size"
            >
              <strong>{articles.length}</strong>
              <span>published articles</span>
              <i aria-hidden="true" />
              <strong>{availableTopics.length}</strong>
              <span>topics</span>
            </div>
          </div>
        </section>

        <section
          className="article-index-browser content"
          aria-labelledby="article-index-heading"
        >
          <div className="article-index-browser__controls">
            <header className="article-index-browser__heading">
              <h2 id="article-index-heading">Browse the library</h2>
            </header>

            <details className="article-index-filter-panel">
              <summary>
                <span className="article-index-filter-panel__label">
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <path d="M4 6h16M7 12h10M10 18h4" />
                  </svg>
                  Filter &amp; sort
                </span>
                <svg
                  className="article-index-filter-panel__chevron"
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                >
                  <path d="m7 9 5 5 5-5" />
                </svg>
              </summary>

              <div className="article-index-toolbar">
                <div
                  className="article-index-filters"
                  aria-label="Filter articles by topic"
                >
                  <button
                    type="button"
                    className={activeTopic === "all" ? "is-active" : ""}
                    onClick={() => setActiveTopic("all")}
                    aria-pressed={activeTopic === "all"}
                  >
                    All topics <span>{articles.length}</span>
                  </button>
                  {availableTopics.map(topic => (
                    <button
                      type="button"
                      className={`article-index-filter--${topic.tone}${
                        activeTopic === topic.id ? " is-active" : ""
                      }`}
                      onClick={() => setActiveTopic(topic.id)}
                      aria-pressed={activeTopic === topic.id}
                      key={topic.id}
                    >
                      {topic.label} <span>{topic.count}</span>
                    </button>
                  ))}
                </div>

                <label className="article-index-sort">
                  <span>Order</span>
                  <select
                    value={sortOrder}
                    onChange={event => setSortOrder(event.target.value)}
                  >
                    {Object.entries(SORT_LABELS).map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </details>

            <p className="article-index-results" aria-live="polite">
              Showing {visibleArticles.length}{" "}
              {visibleArticles.length === 1 ? "article" : "articles"}
              {activeTopic === "all"
                ? ""
                : ` about ${topicDetails(activeTopic).label.toLowerCase()}`}
            </p>
          </div>

          <div className="article-index-groups">
            {groups.map((group, groupIndex) => (
              <section
                className={`article-index-group article-index-group--${group.tone}`}
                key={group.id}
              >
                <header>
                  <span>{String(groupIndex + 1).padStart(2, "0")}</span>
                  <h2>{group.label}</h2>
                  <p>
                    {group.articles.length}{" "}
                    {group.articles.length === 1 ? "article" : "articles"}
                  </p>
                </header>
                <div className="article-index-grid">
                  {group.articles.map(article => (
                    <ArticleCard article={article} key={article.slug} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>
      </main>
    </Layout>
  )
}

export default ArticlesPage

export const pageQuery = graphql`
  query ArticlesIndex {
    site {
      siteMetadata {
        title
      }
    }
    allMarkdownRemark(
      filter: {
        frontmatter: { status: { eq: "publish" }, type: { ne: "topic" } }
      }
      sort: { fields: [frontmatter___title], order: ASC }
    ) {
      nodes {
        fields {
          slug
        }
        generatedExcerpt: excerpt(pruneLength: 260)
        frontmatter {
          title
          description
          excerpt
          topics
          date
          updated
        }
      }
    }
  }
`
